import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { debounce } from 'lodash';
import {
  withStyles,
  CircularProgress,
  Typography,
  AccordionSummary,
  AccordionDetails,
  AccordionActions,
  Accordion,
  Grid,
  Divider,
  Tooltip,
  TextField,
  Button,
} from '@material-ui/core';
import Select from 'react-select';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ArrowBack from '@material-ui/icons/KeyboardArrowLeft';
import ArrowForward from '@material-ui/icons/KeyboardArrowRight';
import {
  getEventTemplates,
  getEventTemplateSubscriptions,
} from '@pingum/app-state-management/eventTemplate/actions';

import styles from './styles';

import StatusBadge from '../../../widgets/StatusBadge';
import Tag from '../../../widgets/Tag';
import DisplayMessage from '../../../widgets/DisplayMessage';
import User from '../../../widgets/User';
import UserEventSubscriptionPopover from '../../../widgets/UserEventSubscriptionPopover';

export const DEBOUNCE_TIME = 500;

export const getUser = (subscription, tenantUsers) => {
  const filterUser = tenantUsers.filter((tenantUser) => tenantUser.auth0UserId === subscription.auth0UserId);
  if (filterUser.length === 0) {
    return {
      auth0UserId: subscription.auth0UserId,
      email: subscription.userEmail,
    };
  }
  return filterUser[0];
};

export const getTenantUserOptions = (tenantUsers, subscriptions) => tenantUsers
  .filter((tenantUser) => subscriptions.filter((subscription) => subscription.auth0UserId === tenantUser.auth0UserId).length === 0)
  .map((tenantUser) => ({
    value: tenantUser.auth0UserId,
    label: tenantUser.userFullName,
  }));

export const getPaginationInfo = (eventTemplates) => {
  const startingNumber = eventTemplates.number * eventTemplates.size;
  const endingNumber = startingNumber + eventTemplates.numberOfElements;
  return `showing ${startingNumber}-${endingNumber} of ${eventTemplates.totalElements}`;
};

export const buildEventTemplateItem
  = (
    classes,
    eventTemplate,
  ) => (
    <Grid container spacing={4} key={eventTemplate.id}>
      <Grid item xs={8} sm={4} className={classes.eventTemplateGridItem}>
        <Typography variant="h4">
          {eventTemplate.name}
        </Typography>
        <span>{eventTemplate.description}</span>
      </Grid>
      <Grid item xs={8} sm={4} className={classes.eventTemplateGridItem}>
        <StatusBadge
          icon={eventTemplate.defaultStartStatus.icon}
          iconColor={
              eventTemplate.defaultStartStatus.iconColor
            }
        />
      </Grid>
      <Grid item xs={8} sm={4} className={classes.eventTemplateGridItem}>
        {eventTemplate.tags.map((tag) => (
          <Tag
            key={`tag-${tag.id}-${eventTemplate.id}`}
            tag={tag}
            icon={tag.tagType.icon}
            color={tag.tagType.iconColor}
          />
        ))}
      </Grid>
    </Grid>
  );

export class OrganizationEventTemplateSubscriptionsCardComponent extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      expanded: null,
      subscriptionAnchorEl: null,
      pagination: {
        page: 0,
        size: 10,
        search: '',
        sort: ['name,asc'],
      },
      searchText: '',
    };

    this.props.getEventTemplates(false, this.state.pagination);
  }

  componentDidMount() {
    this.debouncedSearchEventTemplates = debounce(this.searchEventTemplates, DEBOUNCE_TIME);
  }

  handleLoadSubscriptions = (eventTemplateId) => {
    this.setState((prevState) => ({
      expanded: prevState.expanded === eventTemplateId
        ? null
        : eventTemplateId,
    }
    ), () => {
      this.props.getEventTemplateSubscriptions(eventTemplateId);
    });
  }

  handleAddEventTemplateSubscription = (event) => {
    this.props.updateEventTemplateSubscriptions(event.value, { eventTemplates: { add: [this.state.expanded] } }, 'Successfully subscribed user to workflow');
  }

  handleRemoveEventTemplateSubscription = (auth0UserId) => {
    this.props.updateEventTemplateSubscriptions(auth0UserId, { eventTemplates: { remove: [this.state.expanded] } }, 'Successfully removed user subscription to workflow');
  }

  handleLoadUserSubscriptions = (event, auth0UserId, email) => {
    this.setState({ subscriptionAnchorEl: event.currentTarget });
    this.props.getUserSubscription(auth0UserId, email);
  }

  handleCloseSubscriptionPopover = () => {
    this.setState({ subscriptionAnchorEl: null });
  }

  handleSearchChange = (event) => {
    this.setState({ searchText: event.target.value }, () => {
      this.debouncedSearchEventTemplates();
    });
  }

  searchEventTemplates = () => {
    this.setState((prevState) => ({
      pagination: {
        ...prevState.pagination,
        search: prevState.searchText,
        page: 0,
      },
    }), () => {
      this.props.getEventTemplates(false, this.state.pagination);
    });
  }

  handlePageNext = () => {
    this.setState((prevState) => ({
      pagination: {
        ...prevState.pagination,
        page: prevState.pagination.page + 1,
      },
    }), () => {
      this.props.getEventTemplates(false, this.state.pagination);
    });
  }

  handlePagePrevious = () => {
    this.setState((prevState) => ({
      pagination: {
        ...prevState.pagination,
        page: prevState.pagination.page - 1,
      },
    }), () => {
      this.props.getEventTemplates(false, this.state.pagination);
    });
  }

  render() {
    const {
      classes,
      isFetchingEventTemplates,
      eventTemplates,
      eventTemplateSubscriptions,
      isFetchingEventTemplateSubscriptions,
      tenantUsers,
      userSubscription,
      isFetchingUserSubscription,
      eventTypes,
    } = this.props;
    return (
      <div className={classes.root}>
        <Typography variant="h3" className={classes.catagoryTitle} id="event-templates-group-title" gutterBottom>
          Workflows
          <TextField
            id="search-event-templates"
            label="Search"
            className={classes.searchField}
            value={this.state.searchText}
            onChange={this.handleSearchChange}
            margin="normal"
            variant="outlined"
            type="search"
          />
        </Typography>
        {
            isFetchingEventTemplates
              ? <div style={{ textAlign: 'center' }}><CircularProgress size={80} /></div>
              : (
                <div>
                  {
                  eventTemplates.content.length === 0
                  && (
                    <DisplayMessage
                      id="no-event-templates-text"
                      title="No Workflows"
                      subTitle="You can create one from the event settings page"
                      icon="info_outline"
                    />
                  )
                }
                  {
                  eventTemplates.content.map((eventTemplate) => (
                    <Accordion
                      key={eventTemplate.id}
                      expanded={this.state.expanded === eventTemplate.id}
                      onChange={() => this.handleLoadSubscriptions(eventTemplate.id)}
                    >
                      <AccordionSummary expandIcon={<Tooltip title="Click to load subscriptions"><ExpandMoreIcon /></Tooltip>}>
                        {buildEventTemplateItem(
                          classes,
                          eventTemplate,
                        )}
                      </AccordionSummary>
                      <AccordionDetails style={{ display: 'block' }}>
                        {isFetchingEventTemplateSubscriptions
                          ? <div style={{ textAlign: 'center' }}><CircularProgress size={80} /></div>
                          : eventTemplateSubscriptions.map((subscription) => {
                            const tenantUser = getUser(subscription, tenantUsers);
                            return (
                              <User
                                key={tenantUser.auth0UserId}
                                chip
                                user={tenantUser}
                                showDelete
                                onDelete={() => {
                                  this.handleRemoveEventTemplateSubscription(tenantUser.auth0UserId);
                                }}
                                onClick={(e) => {
                                  this.handleLoadUserSubscriptions(
                                    e,
                                    tenantUser.auth0UserId,
                                    tenantUser.email,
                                  );
                                }}
                              />
                            );
                          })}
                      </AccordionDetails>
                      <Divider />
                      <AccordionActions>
                        <div className={classes.tenantUserSelect}>
                          <Select
                            value={null}
                            placeholder="Select users to subscribe to workflow"
                            onChange={this.handleAddEventTemplateSubscription}
                            options={getTenantUserOptions(tenantUsers, eventTemplateSubscriptions)}
                          />
                        </div>
                      </AccordionActions>
                    </Accordion>
                  ))
                }
                  <UserEventSubscriptionPopover
                    subscriptionAnchorEl={this.state.subscriptionAnchorEl}
                    onClose={this.handleCloseSubscriptionPopover}
                    userSubscription={userSubscription}
                    isFetchingUserSubscription={isFetchingUserSubscription}
                    eventTypes={eventTypes}
                  />
                </div>
              )
          }

        {
          !isFetchingEventTemplates
            && (
              <div className={classes.paginationContainer}>
                <Button className={classes.paginationButton} size="small" disabled={eventTemplates.number === 0} onClick={this.handlePagePrevious}>
                  <ArrowBack />
                  Back
                </Button>
                <Button className={classes.paginationButton} size="small" onClick={this.handlePageNext} disabled={eventTemplates.totalPages === (eventTemplates.number + 1)}>
                  Next
                  <ArrowForward />
                </Button>
                <div className={classes.alignCenter}>{getPaginationInfo(eventTemplates)}</div>
              </div>
            )
        }
      </div>
    );
  }
}

OrganizationEventTemplateSubscriptionsCardComponent.propTypes = {
  classes: PropTypes.object,
  getEventTemplates: PropTypes.func,
  isFetchingEventTemplates: PropTypes.bool,
  eventTemplates: PropTypes.object,
  getEventTemplateSubscriptions: PropTypes.func,
  eventTemplateSubscriptions: PropTypes.array,
  isFetchingEventTemplateSubscriptions: PropTypes.bool,
  tenantUsers: PropTypes.array,
  updateEventTemplateSubscriptions: PropTypes.func,
  getUserSubscription: PropTypes.func,
  userSubscription: PropTypes.object,
  isFetchingUserSubscription: PropTypes.bool,
  eventTypes: PropTypes.array,
};

OrganizationEventTemplateSubscriptionsCardComponent.defaultProps = {
  classes: {},
  getEventTemplates: () => {},
  isFetchingEventTemplates: true,
  eventTemplates: {
    content: [],
    numberOfElements: 0,
    totalElements: 0,
    size: 0,
  },
  getEventTemplateSubscriptions: () => {},
  eventTemplateSubscriptions: [],
  isFetchingEventTemplateSubscriptions: false,
  tenantUsers: [],
  updateEventTemplateSubscriptions: () => {},
  getUserSubscription: () => {},
  userSubscription: { disableNotifications: false },
  isFetchingUserSubscription: true,
  eventTypes: [],
};

export const mapStateToProps = (state) => ({
  isFetchingEventTemplates: state.eventTemplate.isFetchingEventTemplates,
  eventTemplates: state.eventTemplate.eventTemplatesPageable,
  eventTemplateSubscriptions: state.eventTemplate.eventTemplateSubscriptions,
  isFetchingEventTemplateSubscriptions: state.eventTemplate.isFetchingEventTemplateSubscriptions,
  tenantUsers: state.tenant.tenantUsers,
  userSubscription: state.userSubscription.userSubscription,
  isFetchingUserSubscription: state.userSubscription.isFetchingUserSubscription,
  eventTypes: state.eventType.eventTypes,
});

export const mapDispatchToProps = (dispatch) => ({
  getEventTemplates: (includeArchived, pagination) => dispatch(getEventTemplates(includeArchived, pagination)),
  getEventTemplateSubscriptions: (eventTemplateId) => dispatch(getEventTemplateSubscriptions(eventTemplateId)),
});

export default withStyles(styles)(connect(
  mapStateToProps,
  mapDispatchToProps,
)(OrganizationEventTemplateSubscriptionsCardComponent));
